import type { NavItem } from '@/types'

// All public website wording lives here so copy changes happen in one place.
// Source of truth: BUCKY AI Master Website Build Prompt v2.0, then the Website Visual Blueprint.

export const brand = {
    name: 'BUCKY AI',
    product: 'BUCKY AI PROMOTER',
    tagline: 'An AI promoter that turns attention into action.',
    phrase: 'PLAY. DISCOVER. CONNECT.',
    description: 'BUCKY AI PROMOTER is an emerging interactive AI promotion platform for physical businesses.',
    founder: {
        name: 'Hayford Yii',
        title: 'Founder | Bucky AI'
    },
    copyright: '© 2026 BUCKY AI',
    disclaimer: 'Visuals on this website are concept illustrations of an experience in development, not screenshots of a live product.'
} as const

export const statusLabels = {
    inDevelopment: 'IN DEVELOPMENT',
    futureVision: 'FUTURE VISION',
    concept: 'Concept illustration'
} as const

export const navItems: NavItem[] = [
    { label: 'Concept', sectionId: 'concept' },
    { label: 'Play-to-Pitch', sectionId: 'play-to-pitch' },
    { label: 'Businesses', sectionId: 'businesses' },
    { label: 'Vision', sectionId: 'vision' },
    { label: 'Contact', sectionId: 'contact' }
]

export const hero = {
    label: 'INTERACTIVE AI PROMOTION',
    headingLead: 'An AI promoter that turns',
    headingEmphasis: 'attention into action.',
    sentence: 'Meet Bucky AI — a new approach to engaging customers and promoting physical businesses.',
    primaryCta: 'Discover Play-to-Pitch',
    secondaryCta: 'Contact Bucky AI',
    scrollCue: 'Scroll to explore'
} as const

export type ConceptStageIcon = 'attract' | 'engage' | 'discover' | 'connect' | 'guide'

export const concept = {
    eyebrow: 'What is Bucky AI?',
    headlineLead: 'Meet Bucky AI.',
    headlineEmphasis: 'Your Digital Promoter.',
    highlights: ['Proactive engagement', 'Business-approved information', 'Kiosk, tablet or compatible screen'],
    statement: 'An AI promoter that turns attention into action.',
    subnav: [
        { label: 'Overview', id: 'concept' },
        { label: 'The problem', id: 'bucky-problem' },
        { label: 'How it works', id: 'how-bucky-works' },
        { label: 'What makes it different', id: 'bucky-difference' },
        { label: 'Industries', id: 'bucky-industries' },
        { label: 'The experience', id: 'bucky-experience' },
        { label: 'The bigger vision', id: 'bucky-platform' }
    ],
    lead: 'Bucky AI is an AI-powered promotional platform designed to help businesses engage people in physical spaces through interactive digital experiences and conversational AI.',
    description: [
        {
            kicker: 'Proactive by design',
            text: 'Unlike a traditional chatbot that waits for someone to open a conversation, Bucky is designed around proactive engagement. It gives passersby a reason to stop, interact and discover what a business has to offer.'
        },
        {
            kicker: 'On any compatible screen',
            text: 'Through a digital kiosk, tablet or compatible screen, Bucky can introduce a business, communicate verified product information, highlight promotions, answer customer questions, display visual content and guide interested visitors towards a next step.'
        },
        {
            kicker: 'Presence with consistency',
            text: 'The experience is designed to combine the engaging presence of a human promoter with the availability and consistency of a digital system.'
        },
        {
            kicker: 'Alongside your team',
            text: 'Bucky is not intended to replace every human interaction. Instead, it aims to give businesses another way to communicate with visitors, especially in busy environments where staff may be occupied, promotions need consistent presentation or customer attention is difficult to capture.'
        },
        {
            kicker: 'Built to adapt',
            text: 'From retail stores and restaurants to shopping malls, exhibitions, product launches and service businesses, Bucky is being developed as an adaptable promotional platform that can be configured around different businesses and their objectives.'
        }
    ],
    problem: {
        heading: 'Attention is valuable. But getting it is difficult.',
        items: [
            {
                title: 'Limited human availability',
                points: [
                    'Employees have other responsibilities.',
                    'Human promoters can only engage a limited number of visitors at a time.',
                    'Repeated explanations can become tiring.'
                ]
            },
            {
                title: 'Passive digital experiences',
                points: [
                    'Traditional information screens often display content without meaningful interaction.',
                    'Ordinary chatbots usually wait for customers to initiate conversations.',
                    'Visitors may walk past without discovering what is being offered.'
                ]
            },
            {
                title: 'Inconsistent promotion',
                points: [
                    'Different staff members may explain products differently.',
                    'Promotions and important product details may not always be communicated consistently.',
                    'Businesses need a more structured way to present verified information.'
                ]
            }
        ]
    },
    howItWorks: {
        heading: 'How Bucky works',
        intro: 'Five stages take a visitor from noticing the screen to a clear next step.',
        stages: [
            {
                number: '01',
                title: 'Attract',
                icon: 'attract',
                text: 'Bucky uses an interactive promotional experience to encourage passersby to stop and engage.'
            },
            {
                number: '02',
                title: 'Engage',
                icon: 'engage',
                text: 'Visitors can interact with Bucky through a tablet, kiosk or compatible display.'
            },
            {
                number: '03',
                title: 'Discover',
                icon: 'discover',
                text: 'Bucky introduces relevant business information, products, services, offers and visual content.'
            },
            {
                number: '04',
                title: 'Connect',
                icon: 'connect',
                text: 'Visitors can ask questions, explore displayed materials and receive guidance towards a relevant next step.'
            },
            {
                number: '05',
                title: 'Guide',
                icon: 'guide',
                text: 'Bucky can help direct visitors towards a store location, promotion, product information or another business-defined action.'
            }
        ] as { number: string, title: string, icon: ConceptStageIcon, text: string }[]
    },
    difference: {
        eyebrow: 'What makes Bucky different?',
        heading: 'A promoter, not just a chatbot.',
        note: 'These are typical differences in product approach, not limitations of every chatbot.',
        chatbot: {
            title: 'Traditional chatbot',
            points: [
                'Waits for a user to initiate a conversation.',
                'Primarily responds to incoming questions.',
                'Often operates as a standalone chat interface.',
                'Usually depends on the customer already knowing what to ask.'
            ]
        },
        bucky: {
            title: 'Bucky AI Promoter',
            points: [
                'Designed to encourage engagement proactively.',
                'Introduces the business and its offerings.',
                'Combines interaction with visual promotional content.',
                'Can guide customers through a structured promotional journey.',
                'Is designed for physical business environments.'
            ]
        }
    },
    industries: {
        heading: 'Designed for different industries',
        intro: 'Bucky is industry-independent. The same promoter adapts to each environment and its objective.',
        cta: 'Explore the business examples'
    },
    experience: {
        heading: 'The Bucky experience',
        intro: 'A tablet or kiosk where a short game draws people in, Bucky speaks through compact subtitles, and products appear exactly when they are relevant.',
        callouts: [
            { title: 'Promotional experience', text: 'A business-themed game fills most of the screen and invites people to play.' },
            { title: 'Top-centre subtitles', text: 'Bucky speaks through a compact two-line subtitle panel that never covers the game.' },
            { title: 'Bucky in the corner', text: 'The orange mascot stays a small circle until a visitor taps it.' },
            { title: 'Customer interaction', text: 'Visitors can ask questions and get answers based on business-approved information.' },
            { title: 'Visual asset card', text: 'Product images, brochures and offers appear as clear cards on screen.' }
        ],
        sampleQuestion: 'Is there an offer for me?',
        sampleAnswer: "There may be one you're eligible for. Here are the details and conditions.",
        assetTitle: 'Product photograph',
        assetType: 'Asset 01 · Image'
    },
    platform: {
        heading: 'More than a digital screen. A promotional platform.',
        intro: 'Bucky is being shaped into a platform that brings games, promotion and customer journeys together.',
        cta: 'See the full vision',
        items: [
            { name: 'Interactive Games', text: 'Experiences that encourage people to approach and participate.', status: 'inDevelopment' },
            { name: 'Campaign Management', text: 'Business promotions and campaigns.', status: 'futureVision' },
            { name: 'AI Pitch Strategy', text: 'Configurable promotional conversations and customer journeys.', status: 'futureVision' },
            { name: 'Business Content and Assets', text: 'Structured product information, images and promotional materials.', status: 'futureVision' },
            { name: 'Customer Journey and Analytics', text: 'Insights into engagement and promotional performance.', status: 'futureVision' }
        ] as { name: string, text: string, status: 'inDevelopment' | 'futureVision' }[],
        statusNote: 'Interactive Games are in development through the Play-to-Pitch prototype. The other capabilities are planned and not yet available.'
    }
} as const

export type StageId = 'attract' | 'engage' | 'discover' | 'connect'

export const playToPitch = {
    eyebrow: 'Play-to-Pitch',
    heading: 'The game opens the door. Bucky starts the conversation.',
    principle: 'The game attracts attention. Bucky uses that opportunity to engage the customer. The interaction can lead to a meaningful business action.',
    demoLabel: 'Concept demonstration. Sample responses only — not the live product.',
    stages: [
        {
            id: 'attract',
            number: '01',
            title: 'Attract',
            short: 'Choose to play.',
            description: 'A visitor notices an engaging, business-themed game and chooses to play.',
            takeaway: 'A game is the invitation.'
        },
        {
            id: 'engage',
            number: '02',
            title: 'Engage',
            short: 'Bucky appears unobtrusively.',
            description: 'The game stays primary. Bucky appears as a small corner companion and speaks at relevant moments.',
            takeaway: 'The overlay never blocks play.'
        },
        {
            id: 'discover',
            number: '03',
            title: 'Discover',
            short: 'Ask and explore.',
            description: 'Tap Bucky to pause the game and ask questions about products, offers or events.',
            takeaway: 'Conversation is optional.'
        },
        {
            id: 'connect',
            number: '04',
            title: 'Connect',
            short: 'Take a relevant next step.',
            description: 'Bucky guides the visitor to a counter, product, event or eligible reward.',
            takeaway: 'A clear next step.'
        }
    ] as { id: StageId, number: string, title: string, short: string, description: string, takeaway: string }[],
    visitorSees: {
        title: 'What the visitor sees',
        points: [
            'The game fills most of the screen. Bucky stays a small circle in the corner.',
            'Subtitles sit in a compact panel at the top centre, two lines at most.',
            'Tapping Bucky pauses the game and opens the conversation. Closing it returns to the game with the score kept.'
        ]
    },
    tryIt: 'Try it: pop a bubble, tap Bucky, pick a question, then close the panel.'
} as const

export type DemoLanguage = 'en' | 'ms'

export const demoLanguages: { id: DemoLanguage, label: string, short: string }[] = [
    { id: 'en', label: 'English', short: 'EN' },
    { id: 'ms', label: 'Bahasa Melayu', short: 'BM' }
]

// Prewritten sample lines for the concept demonstration. Not generated, not live.
export const demoScript = {
    gameTitle: { en: 'Bubble Discovery', ms: 'Bubble Discovery' },
    tapToPlay: { en: 'Tap to play', ms: 'Tekan untuk bermain' },
    attract: {
        en: 'Tap to play and see what you can discover.',
        ms: 'Tekan untuk bermain dan lihat apa yang boleh anda temui.'
    },
    engage: {
        en: 'Nice one! Tap me anytime to discover what is new here.',
        ms: 'Bagus! Tekan saya bila-bila masa untuk mengetahui apa yang baharu di sini.'
    },
    greeting: {
        en: "Hi, I'm Bucky. What would you like to know?",
        ms: 'Hai, saya Bucky. Apa yang anda ingin tahu?'
    },
    connect: {
        en: "Great choice. Here's your next step.",
        ms: 'Pilihan yang bagus. Ini langkah seterusnya untuk anda.'
    },
    paused: { en: 'Game paused', ms: 'Permainan dijeda' },
    score: { en: 'Score', ms: 'Mata' },
    nextStep: { en: 'Your next step', ms: 'Langkah seterusnya' },
    backToGame: { en: 'Back to game', ms: 'Kembali ke permainan' },
    seeNextStep: { en: 'See my next step', ms: 'Lihat langkah seterusnya' },
    questions: [
        {
            id: 'new',
            question: { en: "What's new here today?", ms: 'Apa yang baharu di sini hari ini?' },
            answer: {
                en: "Today's featured products are near the front counter. I can show you the details.",
                ms: 'Produk pilihan hari ini ada berhampiran kaunter hadapan. Saya boleh tunjukkan butirannya.'
            }
        },
        {
            id: 'offer',
            question: { en: 'Is there an offer for me?', ms: 'Ada tawaran untuk saya?' },
            answer: {
                en: "There may be an offer you're eligible for. I'll show the details and conditions first.",
                ms: 'Mungkin ada tawaran yang anda layak. Saya akan tunjukkan butiran dan syaratnya dahulu.'
            }
        },
        {
            id: 'where',
            question: { en: 'Where do I go next?', ms: 'Ke mana saya patut pergi?' },
            answer: {
                en: 'Head to the counter on your left. A team member can help you there.',
                ms: 'Sila ke kaunter di sebelah kiri anda. Kakitangan kami boleh membantu anda di sana.'
            }
        }
    ],
    nextSteps: [
        { en: 'Visit the counter', ms: 'Kunjungi kaunter' },
        { en: 'Discover the featured product', ms: 'Lihat produk pilihan' },
        { en: 'Find the event booth', ms: 'Cari booth acara' },
        { en: 'Check an eligible reward', ms: 'Semak ganjaran yang layak' }
    ],
    sampleOnly: { en: 'Sample responses only', ms: 'Contoh jawapan sahaja' }
} as const

export type BusinessIcon = 'store' | 'utensils' | 'building' | 'presentation' | 'tent' | 'package' | 'briefcase'

export const businesses = {
    eyebrow: 'Business applications',
    heading: 'One concept. Many environments.',
    intro: 'Bucky is industry-independent. The same Play-to-Pitch journey adapts to each environment: its game theme, its message and the next step that makes sense there.',
    label: 'Illustrative scenarios. Bucky AI is in development and not yet deployed at these businesses.',
    items: [
        {
            id: 'retail',
            name: 'Retail stores',
            icon: 'store',
            explanation: 'Turn browsing into guided product discovery.',
            scenario: 'Shoppers collect items in a quick game, then Bucky introduces the featured product and its verified details.',
            subtitle: 'Nice catch! Want to see this week’s featured product?',
            nextStep: 'See the featured product'
        },
        {
            id: 'restaurants',
            name: 'Restaurants and cafés',
            icon: 'utensils',
            explanation: 'Engage guests while they wait.',
            scenario: 'Guests play while waiting, and Bucky shares menu highlights from the owner’s approved information.',
            subtitle: 'Hungry for ideas? I can show today’s menu highlights.',
            nextStep: 'View menu highlights'
        },
        {
            id: 'malls',
            name: 'Shopping malls',
            icon: 'building',
            explanation: 'Help visitors discover stores and events.',
            scenario: 'A mall-wide game draws visitors in, and Bucky points them to stores, events and promotions.',
            subtitle: 'Great score! There is an event on level two today.',
            nextStep: 'Find the event'
        },
        {
            id: 'exhibitions',
            name: 'Exhibitions and trade shows',
            icon: 'presentation',
            explanation: 'Draw visitors to the booth and start conversations.',
            scenario: 'A short challenge at the booth gives visitors a reason to stop, and Bucky explains what the exhibitor offers.',
            subtitle: 'Thanks for playing! Want a quick look at what we do?',
            nextStep: 'Talk to the team'
        },
        {
            id: 'booths',
            name: 'Event booths',
            icon: 'tent',
            explanation: 'Give passers-by a reason to stop and play.',
            scenario: 'Passers-by play a quick round, and Bucky introduces the booth and a relevant next step.',
            subtitle: 'You are on a roll! Can I tell you what we have here?',
            nextStep: 'Join the booth activity'
        },
        {
            id: 'launches',
            name: 'Product launches',
            icon: 'package',
            explanation: 'Let people play first, then meet the product.',
            scenario: 'The game reveals the new product piece by piece, and Bucky shares its approved key facts.',
            subtitle: 'You unlocked it! Here are the key facts about the launch.',
            nextStep: 'Explore the new product'
        },
        {
            id: 'services',
            name: 'Service businesses',
            icon: 'briefcase',
            explanation: 'Answer common questions and guide people onward.',
            scenario: 'Visitors play while they wait, and Bucky answers common questions before guiding them to the right staff member.',
            subtitle: 'Good game! Do you have a question about our services?',
            nextStep: 'Speak with our team'
        }
    ] as { id: string, name: string, icon: BusinessIcon, explanation: string, scenario: string, subtitle: string, nextStep: string }[]
} as const

export type ModuleIcon = 'gamepad' | 'calendar' | 'sparkles' | 'folder' | 'route'

export const vision = {
    eyebrow: 'Vision and platform modules',
    heading: 'A flexible promotion platform.',
    intro: 'The long-term direction for Bucky is a set of modules that work together, so each business can shape how it attracts, engages and guides its customers.',
    modules: [
        { name: 'Interactive Games', detail: 'Theme and game choice', icon: 'gamepad' },
        { name: 'Campaign Management', detail: 'Objective and schedule', icon: 'calendar' },
        { name: 'AI Pitch Strategy', detail: 'Approved message flow', icon: 'sparkles' },
        { name: 'Business Content and Assets', detail: 'Assets, offers and facts', icon: 'folder' },
        { name: 'Customer Journey and Analytics', detail: 'Discovery and next action', icon: 'route' }
    ] as { name: string, detail: string, icon: ModuleIcon }[],
    modulesNote: 'Long-term direction, not live or purchasable modules.',
    statement: {
        title: "We're building Bucky AI.",
        body: 'The full platform and website are currently in development. We are working towards bringing the Play-to-Pitch experience to life through an interactive prototype.'
    },
    inDevelopment: [
        'Play-to-Pitch prototype',
        'Bucky AI promoter experience',
        'English and Bahasa Melayu interaction',
        'Independently selectable spoken and subtitle languages'
    ],
    futureVision: [
        'Full promotion platform',
        'Advanced strategy editor',
        'Campaign management',
        'Expanded analytics and customer journeys',
        'Additional business integrations'
    ]
} as const

export const contact = {
    eyebrow: 'Contact Bucky AI',
    heading: "Interested in what we're building?",
    body: 'Business owners, potential partners and collaborators are welcome to get in touch.',
    sectionCta: 'Contact Bucky AI',
    enquiriesStatus: 'Founder enquiries open',
    // Public contact channel, approved by the founder on 5 November 2026.
    // This is the ONE place the WhatsApp destination is defined: the Contact section button and the
    // footer link both read it. Set `enabled` to false to remove the button and link everywhere.
    whatsapp: {
        enabled: true,
        url: 'https://wa.me/qr/C6OENDZIN4QBJ1',
        label: 'Contact Bucky AI',
        hint: 'Opens WhatsApp in a new tab or in the WhatsApp app.'
    }
} as const

export const isWhatsAppEnabled = (): boolean => contact.whatsapp.enabled && contact.whatsapp.url.length > 0
